import { Button } from "@/app/components/shadcn/button";
import { Input } from "@/app/components/shadcn/input";
import { Tag, Ticket, AlertCircle } from "lucide-react";
import Link from "next/link";
import Image from "next/image";
import { toast } from "sonner";
import useModal from "@/app/lib/features/modal/modal.hook";
import useCart from "@/app/lib/features/cart/cart.hook";
import useAuth from "@/app/lib/features/auth/auth.hook";
import AuthRequired from "./AuthRequired";
import { getSoftBadgeColors, resolveColorHex } from "@/app/utils/badgeColors";
import { ReactNode, useState } from "react";
import AppliedDiscounts from "./AppliedDiscounts";
import { trackGeneric } from "@/app/lib/tracking/interactions";
import {
  CartBox,
  SummaryMessage,
  DiscountInputText,
  CouponsInfo,
  AppliedDiscount,
} from "@/app/lib/features/cart/cart.types";

interface OrderSummaryProps {
  cartBox: CartBox[];
  summaryMessages: SummaryMessage[];
  continueShoppingUrl: string;
  discountInputText?: DiscountInputText;
  couponsInfo?: CouponsInfo;
  appliedDiscounts?: AppliedDiscount[];
  isCheckoutDisabled?: boolean;
  checkoutErrors?: string[];
  isSubmitting?: boolean;
  onCheckoutClick?: () => void;
  showDiscountSection?: boolean;
  buttonText?: string;
  buttonColor?: string;
  buttonTextColor?: string;
  flat?: boolean;
  hideButton?: boolean;
  discountOnly?: boolean;
  /**
   * Onay butonunun hemen üstüne yerleştirilecek içerik (ör. checkout'taki
   * sözleşme onayı). Butonu kilitleyen bir alan varsa butondan ayrı bir kartta
   * durmamalı — kullanıcı butona basıp hatayı görünce onayı aramak zorunda kalıyor.
   */
  beforeCheckoutButton?: ReactNode;
}

const OrderSummary = ({
  cartBox,
  summaryMessages,
  continueShoppingUrl,
  discountInputText,
  couponsInfo,
  appliedDiscounts = [],
  isCheckoutDisabled = false,
  checkoutErrors = [],
  isSubmitting = false,
  onCheckoutClick,
  showDiscountSection = true,
  buttonText,
  buttonColor,
  buttonTextColor,
  flat = false,
  hideButton = false,
  discountOnly = false,
  beforeCheckoutButton,
}: OrderSummaryProps) => {
  const { createModal } = useModal();
  const { isAuthenticated } = useAuth();
  const {
    applyDiscount,
    removeDiscountCode,
    isApplyingDiscount,
    isRemovingDiscount,
    getAppliedCoupons,
  } = useCart();
  const [showCouponInput, setShowCouponInput] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [error, setError] = useState("");
  const [discountAuthOpen, setDiscountAuthOpen] = useState(false);

  const appliedCouponIds = getAppliedCoupons();

  const handleOpenCouponsModal = () => {
    if (!isAuthenticated) {
      setDiscountAuthOpen(true);
      return;
    }
    trackGeneric("coupons_open", "click");
    createModal("coupons", {
      title: couponsInfo?.title || "Kuponlarım",
      description:
        couponsInfo?.detail || "Size özel kuponları seçin ve tasarruf edin",
      appliedCoupons: appliedCouponIds,
    });
  };

  const handleApplyDiscountCode = async () => {
    if (!couponCode.trim()) {
      setError("Lütfen bir indirim kodu girin.");
      return;
    }

    setError("");
    const { success, message } = await applyDiscount({ discount_code: couponCode });

    if (success) {
      trackGeneric("coupon_apply", "click");
      toast.success(message || "İndirim kodu başarıyla uygulandı!");
      setCouponCode("");
      setShowCouponInput(false);
    } else {
      const errorMsg = message || "İndirim kodu uygulanamadı!";
      setError(errorMsg);
      toast.error(errorMsg);
    }
  };

  const handleRemoveDiscount = async () => {
    const success = await removeDiscountCode();

    if (success) {
      toast.success("İndirim kaldırıldı");
    } else {
      toast.error("İndirim kaldırılamadı");
    }
  };

  return (
    <div className={flat ? "" : "bg-white rounded-lg lg:rounded-xl shadow-sm border border-gray-200 p-4 lg:p-6 lg:sticky lg:top-6"}>
      {!flat && (
        <h2 className="text-lg lg:text-xl font-semibold text-gray-900 mb-4 lg:mb-6">
          Sipariş Özeti
        </h2>
      )}

      {showDiscountSection && (
        <>
          <div>
            <Button
              onClick={handleOpenCouponsModal}
              variant="outline"
              className="w-full font-medium mb-2"
              size="lg"
              disabled={isApplyingDiscount || isRemovingDiscount}
            >
              <Ticket className="w-4 h-4 mr-2" />
              Kuponlarım
            </Button>
          </div>

          {!discountOnly && (
            <AppliedDiscounts
              discounts={appliedDiscounts}
              onRemove={handleRemoveDiscount}
              isRemoving={isRemovingDiscount}
            />
          )}

          {!showCouponInput ? (
            <div className="mb-4 lg:mb-6">
              <Button
                onClick={() => {
                  if (!isAuthenticated) {
                    setDiscountAuthOpen(true);
                    return;
                  }
                  setShowCouponInput(true);
                }}
                className="font-medium w-full"
                size="lg"
                disabled={isApplyingDiscount || isRemovingDiscount}
              >
                <Tag className="w-4 h-4 mr-2" />
                {discountInputText?.title || "İndirim Kodu Gir"}
              </Button>
            </div>
          ) : (
            <div className="mb-4 lg:mb-6 p-3 lg:p-4 bg-gray-50 rounded-lg">
              <div className="space-y-3">
                <div className="flex gap-2">
                  <Input
                    placeholder={
                      discountInputText?.title || "İndirim kodunuzu girin"
                    }
                    value={couponCode}
                    onChange={(e) => {
                      setCouponCode(e.target.value.toUpperCase());
                      setError("");
                    }}
                    className="flex-1"
                    disabled={isApplyingDiscount}
                  />
                  <Button
                    onClick={handleApplyDiscountCode}
                    disabled={isApplyingDiscount || !couponCode.trim()}
                    className="h-9 md:h-12 w-25"
                  >
                    {isApplyingDiscount
                      ? "Uygulanıyor..."
                      : discountInputText?.button_text || "Uygula"}
                  </Button>
                </div>

                {error && (
                  <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                    <AlertCircle className="w-3 h-3 shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <Button
                  onClick={() => {
                    setShowCouponInput(false);
                    setCouponCode("");
                    setError("");
                  }}
                  variant="link"
                  className="px-0"
                  disabled={isApplyingDiscount}
                >
                  İptal
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {!discountOnly && (
        <>
          <div className="space-y-3 lg:space-y-4 mb-4 lg:mb-6">
            {cartBox.map((box, index) => (
              <div
                key={index}
                className={`flex justify-between text-sm ${
                  box.is_total
                    ? "text-base lg:text-lg font-semibold border-t pt-3 lg:pt-4"
                    : ""
                }`}
              >
                <span>{box.title}</span>
                <span
                  className="font-medium"
                  style={(() => {
                    // API indirim satırlarında color_hex yerine color:"success"
                    // gönderebiliyor; ikisini de destekle.
                    const hex = resolveColorHex(box.color_hex, box.color);
                    return hex ? { color: hex } : undefined;
                  })()}
                >
                  {box.price}
                </span>
              </div>
            ))}
          </div>

          {summaryMessages.length > 0 && (
            <div className="space-y-3 mb-4">
              {summaryMessages.map((message, index) => {
                const colors = message.color_hex
                  ? getSoftBadgeColors(message.color_hex)
                  : { bg: undefined, text: undefined };

                return (
                  <div
                    key={index}
                    className="p-3 rounded-lg"
                    style={{ backgroundColor: colors.bg }}
                  >
                    <div className="flex items-center gap-2 text-sm">
                      {message.icon_full && (
                        <div className="relative w-4 h-4 shrink-0">
                          <Image
                            src={message.icon_full}
                            alt={message.type}
                            fill
                            className="object-contain"
                          />
                        </div>
                      )}
                      <span className="font-medium" style={{ color: colors.text }}>
                        {message.text}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      <AuthRequired
        open={discountAuthOpen}
        onClose={() => setDiscountAuthOpen(false)}
        title="İndirim Kodu Kullanmak İçin Giriş Yapın"
        description="İndirim kodu kullanmak için giriş yapmanız gerekmektedir."
        onContinueClick={() => setDiscountAuthOpen(false)}
      />

      {!hideButton && <div className="space-y-3">
        {beforeCheckoutButton}
        {checkoutErrors.length > 0 && (
          <div className="space-y-1">
            {checkoutErrors.map((err, i) => (
              <div key={i} className="flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                <AlertCircle className="w-3 h-3 shrink-0" />
                <span>{err}</span>
              </div>
            ))}
          </div>
        )}
        {onCheckoutClick ? (
          <Button
            className="w-full h-11 lg:h-12 text-sm lg:text-base font-medium"
            disabled={isSubmitting}
            onClick={onCheckoutClick}
            style={
              buttonColor && buttonTextColor
                ? {
                    backgroundColor: buttonColor,
                    color: buttonTextColor,
                  }
                : undefined
            }
          >
            {isSubmitting ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
                Ödeme hazırlanıyor...
              </span>
            ) : (
              buttonText || "Siparişi Tamamla"
            )}
          </Button>
        ) : (
          <Link
            href={`/account/checkout`}
            className={`block ${isCheckoutDisabled ? "pointer-events-none" : ""}`}
          >
            <Button
              className="w-full h-11 lg:h-12 text-sm lg:text-base font-medium"
              disabled={isCheckoutDisabled}
            >
              Sepeti Onayla
            </Button>
          </Link>
        )}
        <Link href={continueShoppingUrl}>
          <Button
            variant="outline"
            className="w-full h-11 lg:h-12 text-sm lg:text-base"
          >
            Alışverişe Devam Et
          </Button>
        </Link>
      </div>}
    </div>
  );
};

export default OrderSummary;
